import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LotItemProxyService } from '../../proxies/inventory/lot-item.proxy.service';
import { AdditionEntry, AdditionEntryType, ConsumptionEntry, LotItem, YearWeek } from '../../models/inventory/lot-item';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LocalHelperService } from '../../services/local-helper.service';
import { EnumService } from '../../services/enum.service';
import { BreadcrumbService, EnumValues, ModalService, Note, NotesSidebarComponent, UserLite } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import moment from 'moment';
import { UserAuthenticationService } from '../../services/user-authentication.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { EntityDefinitionProxyService } from '../../proxies/item/entity-definiation.proxy.service';
import { EntityDefinitionDto } from '../../models/item/entity-definition';
import { UserGroup } from '../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-items-inventory-detail',
  templateUrl: './items-inventory-detail.component.html',
  styleUrl: './items-inventory-detail.component.scss',
})
export class ItemsInventoryDetailComponent implements OnInit {
  isLoading = 0;
  lotItem: LotItem | null = null;
  // Form
  lotItemForm = new FormGroup({
    id: new FormControl(''),
    number: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    itemDescription: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    packagingType: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    dateReceived: new FormControl<Date | null>(null, {
      validators: [Validators.required],
    }),
    expiryDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
    }),
    itemManufacturedDate: new FormControl<Date | null>(null, {
      validators: [Validators.required],
    }),
    netQuantity: new FormControl<number>(0, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    originalNetQuantity: new FormControl<number>(0, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    grossQuantity: new FormControl<number>(0, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    originalGrossQuantity: new FormControl<number>(0, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    manufacturer: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    providerLabelNumber: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    providerLotId: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    providerName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    invoiceNumber: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    parentKey: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  createdDate: FormControl<Date | null> = new FormControl(null);
  modifiedDate: FormControl<Date | null> = new FormControl(null);
  entityDefinitions: EntityDefinitionDto[] = [];
  // consumptionEntries
  consumptionEntries: ConsumptionEntry[] = [];
  additionEntries: AdditionEntry[] = [];
  readonly AdditionEntryType = AdditionEntryType;
  // Enums
  entitiesTypes: EnumValues;
  packagingTypeEnum: EnumValues;
  packagingTypeOptions: { label: string; value: string }[] = [];
  // properties for notes component
  userLite: UserLite | null;
  notes: Note[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private lotItemProxyService: LotItemProxyService,
    private localHelperService: LocalHelperService,
    private readonly enumService: EnumService,
    private router: Router,
    private userAuthenticationService: UserAuthenticationService,
    private modalService: ModalService,
    private translateService: TranslateService,
    private entityDefinitionProxyService: EntityDefinitionProxyService,
    private breadcrumbService: BreadcrumbService,
  ) {
    this.entitiesTypes = this.enumService.getEnum('EntityType', 'item').values;
    this.packagingTypeEnum = this.enumService.getEnum('PackagingType', 'inventory').values;
    this.packagingTypeOptions = this._setOptionsForPrimeNg(this.packagingTypeEnum as Record<string, string>);
    this.userLite = this.userAuthenticationService.userLite;
  }

  canEdit() {
    // if Admin or Editor
    if (AuthService.hasRole(UserGroup.ItemsInventoryAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(UserGroup.ItemsInventoryViewer)) {
      return false;
    }
    // Default
    return false;
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(({ id }) => {
      if (id !== 'new') {
        this.getLotItemData(id);
      }

      if (!id || id === 'new') {
        this.router.navigate(['/items-inventory']);
      }
    });

    this.entityDefinitionProxyService.getAll().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.entityDefinitions = response.data;
        }
      },
    });
  }

  getEntityDefinitionName(entityDefinitionCode: number): string {
    return this.entityDefinitions.find((ed) => ed.code === entityDefinitionCode)?.name || 'Unknown Entity Definition';
  }

  getAdditionTypeLabel(type: AdditionEntryType): string {
    return AdditionEntryType[type] ?? String(type);
  }

  getLotItemData(id: string) {
    this.isLoading++;
    this.lotItemProxyService.getByLotItemNumber(id).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.success && response.data) {
          this.lotItem = response.data;
          this.breadcrumbService.setCurrent(this.lotItem.number);
          this.patchLotItemData(this.lotItem);
        }
      },
      error: (error) => {
        this.isLoading--;
        console.log('🚀 ~ ItemsInventoryDetailComponent ~ getLotItemData ~ error:', error);
      },
    });
  }

  patchLotItemData(data: LotItem) {
    this.lotItemForm.patchValue({
      number: data.number,
      itemDescription: this.localHelperService.getLocalizedValue(data.item?.itemDescription),
      packagingType: data.packagingType.toString(),
      dateReceived: data.dateReceived ? new Date(data.dateReceived) : null,
      expiryDate: data.expiryDate ? new Date(data.expiryDate) : null, // PBI 4543: ExpiryDate is now a DateTime, no YearWeek conversion
      itemManufacturedDate: data.itemManufacturedDate ? this.getDateFromWeek(data.itemManufacturedDate) : null,
      netQuantity: data.netQuantity,
      originalNetQuantity: data.originalNetQuantity,
      grossQuantity: data.grossQuantity,
      originalGrossQuantity: data.originalGrossQuantity,
      manufacturer: data.manufacturer?.name,
      providerLabelNumber: data.providerLabelNumber,
      providerLotId: data.providerLotId,
      providerName: data.provider?.name,
      invoiceNumber: data.invoice?.invoiceNumber,
      parentKey: data.parentKey?.number,
    });
    // patch audit trail dates
    this.createdDate.setValue(data.createdDate ? new Date(data.createdDate) : null);
    this.createdDate.disable();
    this.modifiedDate.setValue(data.modifiedDate ? new Date(data.modifiedDate) : null);
    this.modifiedDate.disable();
    // consumptionEntries
    this.consumptionEntries = data.consumptionEntries.length > 0 ? data.consumptionEntries : [];
    this.additionEntries = data.additionEntries ?? [];
    // notes
    this.notes = data.notes && data.notes.length > 0 ? data.notes : [];
    // disable lotItemForm
    this.lotItemForm.disable();
  }

  private _setOptionsForPrimeNg(value: Record<string, string>) {
    return Object.keys(value).map((key) => {
      return {
        label: value[key],
        value: key,
      };
    });
  }

  getDateFromWeek(weekDate: YearWeek): Date {
    if (!weekDate) return new Date();
    const newDate = moment(`${weekDate.year}W${weekDate.week}`).toDate();
    return newDate;
  }

  getEntityType(entityDefinitionCode: number): string {
    return (
      this._setOptionsForPrimeNg(this.entitiesTypes as Record<string, string>).find(
        (option) => Number(option.value) === entityDefinitionCode,
      )?.label || ''
    );
  }

  onViewMore(): void {
    this.openNotesModal();
  }

  openNotesModal(): void {
    const modalData = {
      notes: this.notes,
      userLite: this.userLite,
      viewOnly: false,
    };

    this.modalService.open(NotesSidebarComponent, {}, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'noteAdded') {
          this.notes = result.data.notes;
        }
      },
      error: (error) => {
        console.error('Error handling notes modal result:', error);
      },
    });
    setTimeout(() => {
      const modalElement = document.querySelector('.panel.modal') as HTMLElement;
      if (modalElement) modalElement.style.maxWidth = '50%';
      modalElement.style.width = '50%';
    }, 100);
  }

  save() {
    if (this.lotItem) {
      this.isLoading++;
      // Only update notes for now!
      this.lotItem = { ...this.lotItem, notes: this.notes };

      this.lotItemProxyService.putLotItem(this.lotItem).subscribe({
        next: (response) => {
          if (response.success) {
            GlobalMessageUtil.success(
              'Success',
              this.translateService.instant('ItemsInventory_SuccessUpdated', { lotItemNumber: this.lotItem?.number }),
            );
          }
          this.isLoading--;
        },
        error: (error) => {
          console.log('🚀 ~ ItemsInventoryDetailComponent ~ save ~ error:', error);
          this.isLoading--;
        },
      });
    }
  }

  redirectParentKey() {
    this.lotItemProxyService.getByLotNumbers([this.lotItem?.parentKey?.number ?? '']).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const queryParams = response.data[0].id ? { id: response.data[0].id } : {};
          this.router.navigate(['/items-inventory-detail'], { queryParams });
        }
      },
      error: (error) => {
        console.log('🚀 ~ ItemsInventoryDetailComponent ~ redirectParentKey ~ error:', error);
      },
    });
  }

  printLotItemLabel(lotItem: LotItem) {
    if (lotItem.id) {
      this.isLoading++;
      this.lotItemProxyService.getLotItemLabel(lotItem.id).subscribe({
        next: (blob) => {
          this.isLoading--;
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank')?.focus();
        },
      });
    }
  }
}
